import type {
  ComboBoxOption,
  ComboBoxOptions,
  InterstateOnChangeEvent
} from "@dealer-center/core-components";
import { Button, ComboBox, Switch } from "@dealer-center/core-components";
import type {
  Locale,
  LocalizedVehicleData,
  VehicleSearchFilters
} from "@dealer-center/inventory-pages-gvm-interface";
import styled from "styled-components";
import { valueFromComboBox } from "../../util/comboBoxHelper";
import { useSearchParams } from "react-router-dom";
import { saveLiveQueryFilters } from "@dealer-center/inventory-core-utils";
import { useEffect, useMemo } from "react";

const StyledSearchForm = styled.div`
  .search-form {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
    padding-top: 15px;
    padding-bottom: 15px;
    gap: 10px;
  }

  .search-form .switch-stage {
    display: flex;
    align-items: center;
  }

  .search-form .ComboBox {
    min-width: 180px;
    flex-grow: 1;
  }

  .search-form button {
    min-width: 120px;
  }

  @media (max-width: 800px) {
    .search-form {
      flex-direction: column;
    }

    .search-form .ComboBox {
      width: 100%;
    }
  }
`;

function findLocalizedData(locales: Locale[], localeId: string) {
  if (!locales || !localeId) return undefined;
  return locales.find(locale => locale.locale === localeId);
}

function listYears(localeData: Locale | undefined): number[] {
  if (!localeData) return [];
  return localeData.years.map(year => year.year);
}

function listMakes(
  localeData: Locale | undefined,
  year: number | undefined
): string[] {
  if (!localeData || !year) return [];
  const yearData = localeData.years.find(data => data.year === year);
  return yearData?.makes.map(make => make.make) || [];
}

function listModels(
  localeData: Locale | undefined,
  year: number | undefined,
  make: string | undefined
): string[] {
  if (!localeData || !year || !make) return [];
  const yearData = localeData.years.find(data => data.year === year);
  if (!yearData) return [];
  const makeData = yearData.makes.find(
    data => data.make.toLowerCase() === make
  );
  return makeData?.models || [];
}

interface SearchFormProps {
  localizedVehicleData: LocalizedVehicleData;
  isSearching: boolean;
  searchFilters: VehicleSearchFilters;
  setSearchFilters: (data: VehicleSearchFilters) => void;
  searchFiltersCopy: VehicleSearchFilters;
  setSearchFiltersCopy: (data: VehicleSearchFilters) => void;
  resetAllSelected: () => void;
}

export const SearchForm = ({
  localizedVehicleData,
  isSearching,
  searchFilters,
  setSearchFilters,
  searchFiltersCopy,
  setSearchFiltersCopy,
  resetAllSelected
}: SearchFormProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const localeData = findLocalizedData(
    localizedVehicleData.locales,
    searchFilters.locale
  );
  const locales: ComboBoxOption[] = useMemo(
    () =>
      localizedVehicleData.locales.map(l => ({
        value: l.locale,
        label: l.display
      })),
    [localizedVehicleData.locales]
  );
  const years: ComboBoxOption[] = useMemo(
    () =>
      listYears(localeData).map(year => ({
        value: String(year),
        label: String(year)
      })),
    [localeData]
  );
  const makes: ComboBoxOption[] = useMemo(
    () =>
      listMakes(localeData, searchFilters.year).map(make => ({
        value: make.toLowerCase(),
        label: make
      })),
    [localeData, searchFilters.year]
  );
  const models: ComboBoxOption[] = useMemo(
    () =>
      listModels(localeData, searchFilters.year, searchFilters.make).map(
        model => ({ value: model.toLowerCase(), label: model })
      ),
    [localeData, searchFilters.year, searchFilters.make]
  );
  const selectedLocale: ComboBoxOption[] = useMemo(
    () =>
      localeData
        ? [{ value: localeData.locale, label: localeData.display }]
        : [],
    [localeData]
  );
  const selectedYear: ComboBoxOption[] = useMemo(
    () =>
      searchFilters.year
        ? years.filter(option => option.value === String(searchFilters.year))
        : [],
    [years, searchFilters.year]
  );
  const selectedMake: ComboBoxOption[] = useMemo(
    () =>
      searchFilters.make
        ? makes.filter(value => value.value === searchFilters.make)
        : [],
    [makes, searchFilters.make]
  );
  const selectedModel: ComboBoxOption[] = useMemo(
    () =>
      searchFilters.model
        ? models.filter(value => value.value === searchFilters.model)
        : [],
    [models, searchFilters.model]
  );

  useEffect(() => {
    const yearNotFound = !!searchFilters.year && selectedYear.length === 0;
    const makeNotFound = !!searchFilters.make && selectedMake.length === 0;
    const modelNotFound = !!searchFilters.model && selectedModel.length === 0;
    const doReset = yearNotFound || makeNotFound || modelNotFound;

    if (doReset) {
      setSearchFilters({
        ...searchFilters,
        year: yearNotFound ? undefined : searchFilters.year,
        make: makeNotFound ? undefined : searchFilters.make,
        model: modelNotFound ? undefined : searchFilters.model
      });
    }
  }, [
    searchFilters.locale,
    searchFilters.year,
    searchFilters.make,
    searchFilters.model
  ]);

  const handleStageToggle = () => {
    const stage = searchFilters.stage === "QA" ? "LIVE" : "QA";

    setSearchFilters({
      ...searchFilters,
      stage,
      offset: 0,
      page: 1
    });
  };

  const handleInputChange = (
    event: InterstateOnChangeEvent<ComboBoxOptions>
  ) => {
    const name = event.target.name || "";
    const selectedOption = event.target.value;
    const value = valueFromComboBox(selectedOption);

    setSearchFilters({
      ...searchFilters,
      [name]: value && name === "year" ? Number(value) : value,
      offset: 0,
      page: 1
    });
  };

  const handleSearch = () => {
    const filters = { ...searchFilters };
    setSearchFiltersCopy(filters);
    saveLiveQueryFilters(filters);

    const hasFiltersChanged = !(
      searchFilters.stage === searchFiltersCopy.stage &&
      searchFilters.locale === searchFiltersCopy.locale &&
      searchFilters.year === searchFiltersCopy.year &&
      searchFilters.make === searchFiltersCopy.make &&
      searchFilters.model === searchFiltersCopy.model
    );
    if (hasFiltersChanged) resetAllSelected();

    const queryParams: Record<string, string> = {
      stage: searchFilters.stage,
      locale: searchFilters.locale
    };
    if (searchFilters.year) queryParams.year = String(searchFilters.year);
    if (searchFilters.make) queryParams.make = searchFilters.make;
    if (searchFilters.model) queryParams.model = searchFilters.model;
    setSearchParams(queryParams);
  };

  const render = () => {
    const isQA = searchFilters.stage === "QA";

    return (
      <StyledSearchForm>
        <div className="search-form">
          <div className="switch-stage">
            <Switch
              checked={isQA}
              label={isQA ? "QA" : "Live"}
              layout="vertical"
              onClick={handleStageToggle}
            />
          </div>
          <div className="ComboBox">
            <ComboBox
              data-testid={"gvm-sel-locale"}
              disabled={isSearching}
              name="locale"
              options={locales}
              placeholder="Select Locale..."
              value={selectedLocale}
              onChange={handleInputChange}
            />
          </div>
          <div className="ComboBox">
            <ComboBox
              data-testid={"gvm-sel-year"}
              disabled={isSearching}
              name="year"
              options={years}
              placeholder="Select Year..."
              value={selectedYear}
              onChange={handleInputChange}
            />
          </div>
          <div className="ComboBox">
            <ComboBox
              data-testid={"gvm-sel-make"}
              disabled={isSearching}
              name="make"
              options={makes}
              placeholder="Select Make..."
              value={selectedMake}
              onChange={handleInputChange}
            />
          </div>
          <div className="ComboBox">
            <ComboBox
              data-testid={"gvm-sel-model"}
              disabled={isSearching}
              name="model"
              options={models}
              placeholder="Select Model..."
              value={selectedModel}
              onChange={handleInputChange}
            />
          </div>
          <Button
            analyticsConfiguration={{
              eventName: `Search button clicked`
            }}
            buttonStyle="primary"
            data-testid="gvm-btn-search"
            disabled={
              isSearching ||
              !searchFilters.locale ||
              (searchFilters.stage === "LIVE" && !searchFilters.year)
            }
            type="button"
            onClick={handleSearch}
          >
            {isSearching ? "Searching..." : "Search"}
          </Button>
        </div>
      </StyledSearchForm>
    );
  };

  return render();
};
